'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, ChevronDown, Check, ShieldCheck } from 'lucide-react';
import VehicleBrandLogo from '@/components/VehicleBrandLogo';
import CategoryIcon from '@/components/CategoryIcon';

interface CountryMmyteHeroSelectorProps {
  countrySlug: string;
  countryName: string;
  currency: string;
  popularMakes: string[];
}

const YEAR_OPTIONS = [
  '2026', '2025', '2024', '2023', '2022', '2021', '2020',
  '2019', '2018', '2017', '2016', '2015', '2014', '2013',
  '2012', '2011', '2010', '2009', '2008', '2007', '2006',
  '2005', '2004', '2003', '2002', '2001', '2000'
];

import { GLOBAL_MAKES } from '@/lib/catalog';
import { ALL_AUTOMOTIVE_CATEGORIES } from '@/lib/all-categories';

export default function CountryMmyteHeroSelector({
  countrySlug,
  countryName,
  currency,
  popularMakes,
}: CountryMmyteHeroSelectorProps) {
  const router = useRouter();

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMake, setSelectedMake] = useState('Toyota');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Extract all available makes, deduplicating popular makes with alphabetical global makes
  const popularSet = new Set(popularMakes);
  const otherMakes = GLOBAL_MAKES.filter(m => !popularSet.has(m.name)).map(m => m.name).sort();

  // Find models for selected make from comprehensive global catalog
  const selectedMakeObj = GLOBAL_MAKES.find(
    m => m.name.toLowerCase() === selectedMake.toLowerCase() || m.id.toLowerCase() === selectedMake.toLowerCase()
  );
  const currentModels = selectedMakeObj && selectedMakeObj.models && selectedMakeObj.models.length > 0
    ? selectedMakeObj.models
    : ['All Models'];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (selectedMake) params.set('make', selectedMake);
    if (selectedModel) params.set('model', selectedModel);
    if (selectedYear) params.set('year', selectedYear);
    if (selectedCategory) params.set('category', selectedCategory);

    router.push(`/${countrySlug}/shop?${params.toString()}`);
  };

  return (
    <div className="w-full max-w-4xl mx-auto rounded-2xl bg-white p-4 sm:p-6 shadow-sm border border-slate-200 text-slate-900">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className="flex size-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
            <ShieldCheck className="size-4" />
          </span>
          <h2 className="text-sm sm:text-base font-extrabold text-[#0A1B4A]">
            Exact MMYTE Car Fitment Selector
          </h2>
        </div>
        <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
          Guaranteed 100% Fit in {countryName} ({currency})
        </span>
      </div>

      <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* 1. Make Selector */}
        <div className="space-y-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            1. Vehicle Make
          </label>
          <div className="relative">
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
              <VehicleBrandLogo brand={selectedMake} size="xs" variant="raw" className="size-4" />
            </div>
            <select
              value={selectedMake}
              onChange={(e) => {
                setSelectedMake(e.target.value);
                setSelectedModel('');
              }}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 pl-8 pr-8 py-2.5 text-xs font-bold text-[#0A1B4A] hover:bg-slate-100 focus:border-blue-600 focus:bg-white focus:outline-none transition-colors"
            >
              <option value="">Select Make</option>
              <optgroup label={`Popular in ${countryName}`}>
                {popularMakes.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </optgroup>
              <optgroup label="All Vehicle Makes (A–Z)">
                {otherMakes.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </optgroup>
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* 2. Model Selector */}
        <div className="space-y-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            2. Vehicle Model
          </label>
          <div className="relative">
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3 pr-8 py-2.5 text-xs font-bold text-[#0A1B4A] hover:bg-slate-100 focus:border-blue-600 focus:bg-white focus:outline-none transition-colors"
            >
              <option value="">All Models</option>
              {currentModels.map((mdl) => (
                <option key={mdl} value={mdl}>
                  {mdl}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* 3. Year Selector */}
        <div className="space-y-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            3. Model Year
          </label>
          <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3 pr-8 py-2.5 text-xs font-bold text-[#0A1B4A] hover:bg-slate-100 focus:border-blue-600 focus:bg-white focus:outline-none transition-colors"
            >
              <option value="">Any Year</option>
              {YEAR_OPTIONS.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* 4. Category Selector with CategoryIcon indicator */}
        <div className="space-y-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            4. Part Category
          </label>
          <div className="relative">
            {selectedCategory && (
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <CategoryIcon category={selectedCategory} size="xs" variant="raw" className="size-4" />
              </div>
            )}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 ${selectedCategory ? 'pl-8' : 'px-3'} pr-8 py-2.5 text-xs font-bold text-[#0A1B4A] hover:bg-slate-100 focus:border-blue-600 focus:bg-white focus:outline-none transition-colors`}
            >
              <option value="">All 20 Categories</option>
              {ALL_AUTOMOTIVE_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* CTA Button Row spanning across */}
        <div className="col-span-1 sm:col-span-2 lg:col-span-4 pt-2">
          <button
            type="submit"
            className="w-full py-3 px-6 rounded-xl bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25 transition-all hover:scale-[1.01] active:scale-[0.99]"
          >
            <Search className="size-4" />
            <span>Search Verified In-Stock Parts in {countryName}</span>
          </button>
        </div>
      </form>

      {/* Quick Model Shortcut Tags */}
      <div className="mt-3.5 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-slate-400 font-semibold">Quick Fitments:</span>
        {[
          { label: 'Toyota Corolla 2014', make: 'Toyota', model: 'Corolla', year: '2014' },
          { label: 'Toyota Corolla 2017', make: 'Toyota', model: 'Corolla', year: '2017' },
          { label: 'Toyota Corolla 2009', make: 'Toyota', model: 'Corolla', year: '2009' },
          { label: 'Honda Civic', make: 'Honda', model: 'Civic', year: '' },
          { label: 'Mercedes C-Class', make: 'Mercedes-Benz', model: 'C-Class', year: '' },
        ].map((item, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => {
              setSelectedMake(item.make);
              setSelectedModel(item.model);
              setSelectedYear(item.year);
              const p = new URLSearchParams();
              p.set('make', item.make);
              if (item.model) p.set('model', item.model);
              if (item.year) p.set('year', item.year);
              router.push(`/${countrySlug}/shop?${p.toString()}`);
            }}
            className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 font-semibold transition-colors flex items-center gap-1.5"
          >
            <VehicleBrandLogo brand={item.make} size="xs" variant="raw" className="size-3" />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
